import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export interface Crumb {
  /** Descriptive, human-readable label — never "Click Here" / "Read More". */
  name: string;
  /** Root-relative path. Omit on the last crumb (current page). */
  href?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
  /** Visual variant. Defaults to "light" (works on white/zinc-50). */
  variant?: "light" | "dark";
  className?: string;
}

const SITE_ORIGIN = "https://www.juanthetrailerguy.com";

/**
 * Accessible breadcrumb navigation + BreadcrumbList JSON-LD.
 *
 * - Renders <nav aria-label="Breadcrumb"> with an ordered list.
 * - Current page uses aria-current="page" and is not a link.
 * - Emits schema.org/BreadcrumbList for SEO rich results.
 */
export default function Breadcrumbs({
  items,
  variant = "light",
  className = "",
}: BreadcrumbsProps) {
  if (!items?.length) return null;

  const isDark = variant === "dark";
  const textBase = isDark ? "text-white/85" : "text-muted-foreground";
  const linkHover = isDark ? "hover:text-white" : "hover:text-foreground";
  const currentColor = isDark ? "text-white" : "text-foreground";
  const separatorColor = isDark ? "text-white/90" : "text-muted-foreground";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.href
        ? (it.href.startsWith("http") ? it.href : `${SITE_ORIGIN}${it.href.startsWith("/") ? "" : "/"}${it.href}`)
        : undefined,
    })),
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className={`text-sm ${textBase} ${className}`}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={`${item.name}-${i}`} className="flex items-center gap-1.5">
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className={`font-semibold ${linkHover} transition`}
                >
                  {item.name}
                </Link>
              ) : (
                <span aria-current="page" className={`font-semibold ${currentColor}`}>
                  {item.name}
                </span>
              )}
              {!isLast && (
                <ChevronRight aria-hidden="true" className={`h-4 w-4 ${separatorColor}`} />
              )}
            </li>
          );
        })}
      </ol>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </nav>
  );
}